import React, { useEffect, useState, useCallback } from "react";
import { api, fmtMoney, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ShoppingCart, Minus, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { logError } from "@/lib/log";
import { ProductDetailDialog } from "@/components/ProductDetailDialog";
import { mediaStill } from "@/lib/livePhotoMedia";

/**
 * Customer portal: order products from the catalog.
 * Orders land as PENDING tickets until staff approves them.
 */
export default function PortalProductOrder({ locked, lockReason, onChanged, showSearch = false }) {
  const [products, setProducts] = useState([]);
  const [qty, setQty] = useState({}); // product_id -> quantity
  const [notes, setNotes] = useState("");
  const [search, setSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [viewing, setViewing] = useState(null); // read-only product viewer

  const load = useCallback(() => {
    api.get("/portal/products").then((r) => setProducts(r.data || [])).catch((e) => logError("Products load failed:", e));
  }, []);
  useEffect(() => { load(); }, [load]);

  const setQ = (id, v) => setQty((p) => ({ ...p, [id]: Math.max(0, v) }));
  const selected = products.filter((p) => (qty[p.id] || 0) > 0);
  const total = selected.reduce((s, p) => s + Number(p.price) * (qty[p.id] || 0), 0);
  const q = search.trim().toLowerCase();
  const visible = q
    ? products.filter((p) => (p.name || "").toLowerCase().includes(q) || (p.description || "").toLowerCase().includes(q))
    : products;

  const submit = async () => {
    if (selected.length === 0) return toast.error("Select at least one product");
    setSubmitting(true);
    try {
      await api.post("/portal/orders", {
        items: selected.map((p) => ({ product_id: p.id, quantity: qty[p.id] })),
        notes,
      });
      toast.success("Order placed — check your email for payment instructions");
      setQty({});
      setNotes("");
      onChanged && onChanged();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
    finally { setSubmitting(false); }
  };

  if (products.length === 0) return null;

  return (
    <section className="border border-border" data-testid="portal-products-card">
      <div className="flex items-center gap-2 px-6 py-4 border-b border-border">
        <ShoppingCart className="w-4 h-4 text-primary" />
        <h2 className="font-display text-xl font-bold tracking-tight">Order products</h2>
      </div>
      <div className="p-6 space-y-4">
        {locked && (
          <p className="text-sm text-destructive font-medium" data-testid="portal-products-locked-msg">
            {lockReason === "no_card"
              ? "Orders are locked until a payment card is added to your account (see Payment methods below)."
              : "Orders are locked due to outstanding unpaid invoices. Please pay your open invoices or call the office."}
          </p>
        )}
        {showSearch && products.length > 3 && (
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products…"
                   className="rounded-sm pl-9" data-testid="portal-products-search" />
          </div>
        )}
        <div className="divide-y divide-border border border-border rounded-sm">
          {visible.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">No products match your search.</div>
          )}
          {visible.map((p) => {
            const q = qty[p.id] || 0;
            return (
              <div key={p.id} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`portal-product-${p.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  {(p.image_data_urls || []).length > 0 && (
                    <button type="button" className="relative flex-shrink-0" onClick={() => setViewing(p)}
                            data-testid={`portal-product-img-${p.id}`}>
                      <img src={mediaStill(p.image_data_urls[0])} alt={p.name}
                           className="w-14 h-14 object-cover rounded-sm border border-border" />
                      {p.image_data_urls.length > 1 && (
                        <span className="absolute -bottom-1 -right-1 bg-foreground text-background text-[9px] font-bold rounded-sm px-1">+{p.image_data_urls.length - 1}</span>
                      )}
                    </button>
                  )}
                  <button type="button" className="min-w-0 text-left hover:opacity-80 transition-opacity"
                          onClick={() => setViewing(p)} data-testid={`portal-product-open-${p.id}`}>
                    <div className="font-medium text-sm underline-offset-2 hover:underline">{p.name}</div>
                    {p.description && <div className="text-xs text-muted-foreground truncate">{p.description}</div>}
                    <div className="text-xs font-mono text-muted-foreground mt-0.5">{fmtMoney(p.price)}</div>
                  </button>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button size="icon" variant="outline" className="h-8 w-8 rounded-sm" onClick={() => setQ(p.id, q - 1)}
                          disabled={q === 0} data-testid={`portal-product-minus-${p.id}`}>
                    <Minus className="w-3.5 h-3.5" />
                  </Button>
                  <span className="w-8 text-center mono-num font-semibold text-sm" data-testid={`portal-product-qty-${p.id}`}>{q}</span>
                  <Button size="icon" variant="outline" className="h-8 w-8 rounded-sm" onClick={() => setQ(p.id, q + 1)}
                          data-testid={`portal-product-plus-${p.id}`}>
                    <Plus className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
        {selected.length > 0 && (
          <>
            <div className="flex items-center justify-between">
              <span className="overline">Order total (pre-tax)</span>
              <span className="font-display text-2xl font-black tracking-tighter mono-num" data-testid="portal-products-total">{fmtMoney(total)}</span>
            </div>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
                      className="rounded-sm" placeholder="Notes for the office (optional)" data-testid="portal-products-notes" />
          </>
        )}
        <Button onClick={submit} disabled={submitting || locked || selected.length === 0}
                className="rounded-sm w-full h-11 font-semibold" data-testid="portal-products-submit">
          <ShoppingCart className="w-4 h-4 mr-2" />
          {submitting ? "Submitting…" : "Place order — pending approval"}
        </Button>
        <p className="text-[10px] text-muted-foreground font-mono">
          Orders are reviewed by our office before delivery. You&apos;ll see the status under &ldquo;Your delivery requests&rdquo;.
        </p>
      </div>
      <ProductDetailDialog product={viewing} open={!!viewing} onOpenChange={(v) => !v && setViewing(null)} />
    </section>
  );
}
